import { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, BarChart, Bar } from "recharts";
import { LayoutDashboard, Store, CreditCard, Tag, Shapes, Package, Users, Ticket, FileText, Settings, ScrollText } from "lucide-react";
import { api } from "@/lib/api";
import PortalShell, { PageHead } from "@/components/portal/PortalShell";
import { Kpi, NativeSelect } from "@/components/site/bits";
import { Input } from "@/components/ui/input";
import { AdminMerchants, AdminMerchantDetail } from "./AdminMerchants";
import { AdminSubscriptions, AdminDeals, AdminCategories, AdminPackages, AdminCustomers, AdminCoupons, AdminReports, AdminAudit, AdminSettings } from "./AdminOther";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/admin/merchants", label: "Merchants", icon: Store, id: "merchants" },
  { to: "/admin/subscriptions", label: "Subscriptions", icon: CreditCard, id: "subscriptions" },
  { to: "/admin/deals", label: "Deals", icon: Tag, id: "deals" },
  { to: "/admin/categories", label: "Categories", icon: Shapes, id: "categories" },
  { to: "/admin/packages", label: "Packages", icon: Package, id: "packages" },
  { to: "/admin/customers", label: "Customers", icon: Users, id: "customers" },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket, id: "coupons" },
  { to: "/admin/reports", label: "Reports", icon: FileText, id: "reports" },
  { to: "/admin/audit", label: "Audit log", icon: ScrollText, id: "audit" },
  { to: "/admin/settings", label: "Settings", icon: Settings, id: "settings" },
];

const KPIS = [
  ["total_merchants", "Registered merchants"], ["active_merchants", "Active merchants"], ["awaiting_activation", "Awaiting activation"],
  ["total_branches", "Total branches"], ["total_customers", "Customers"], ["active_deals", "Active deals"], ["deal_views", "Deal views"],
  ["coupons_claimed", "Coupons claimed"], ["coupons_redeemed", "Coupons redeemed"], ["verified_visits", "Verified store visits"],
  ["conversion", "Claim → visit %"], ["expired_coupons", "Expired coupons"], ["active_subscriptions", "Active subscriptions"],
  ["expiring_soon", "Expiring in 7 days"], ["manual_activations", "Manual activations"], ["open_requests", "Open activation requests"],
];

function Dashboard() {
  const [f, setF] = useState({ period: "30d", start: "", end: "", city: "", category_id: "", merchant_id: "" });
  const [d, setD] = useState(null);
  const [cats, setCats] = useState([]);
  const [merchants, setMerchants] = useState([]);
  useEffect(() => {
    api.get("/admin/categories").then((r) => setCats(r.data));
    api.get("/admin/merchants").then((r) => setMerchants(r.data));
  }, []);
  useEffect(() => {
    const p = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/admin/stats", { params: p }).then((r) => setD(r.data));
  }, [f]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-6">
      <PageHead title="Platform overview" sub="All metrics are computed server-side in UTC." />
      <div className="bg-white border border-slate-200 rounded-2xl p-4 grid grid-cols-2 lg:grid-cols-6 gap-3">
        <NativeSelect data-testid="stats-period-select" value={f.period} onChange={set("period")}><option value="today">Today</option><option value="yesterday">Yesterday</option><option value="7d">Last 7 days</option><option value="30d">Last 30 days</option><option value="all">All time</option><option value="custom">Custom dates</option></NativeSelect>
        {f.period === "custom" && <><Input data-testid="stats-start" type="date" value={f.start} onChange={set("start")} /><Input data-testid="stats-end" type="date" value={f.end} onChange={set("end")} /></>}
        <NativeSelect data-testid="stats-city-select" value={f.city} onChange={set("city")}><option value="">All cities</option>{(d?.cities || []).map((c) => <option key={c}>{c}</option>)}</NativeSelect>
        <NativeSelect data-testid="stats-category-select" value={f.category_id} onChange={set("category_id")}><option value="">All categories</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</NativeSelect>
        <NativeSelect data-testid="stats-merchant-select" value={f.merchant_id} onChange={set("merchant_id")}><option value="">All merchants</option>{merchants.map((m) => <option key={m.id} value={m.id}>{m.business_name}</option>)}</NativeSelect>
      </div>
      {!d ? <div className="h-64 bg-slate-100 rounded-2xl animate-pulse" /> : (<>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
          {KPIS.map(([k, l]) => <Kpi key={k} testid={`admin-kpi-${k}`} label={l} value={k === "conversion" ? `${d.kpis[k]}%` : d.kpis[k]} />)}
        </div>
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5 h-80"><p className="font-heading font-bold mb-2">Claims & redemptions</p>
            <ResponsiveContainer width="100%" height="88%"><LineChart data={d.daily}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="date" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Legend /><Line dataKey="claims" stroke="#FF9500" strokeWidth={2} /><Line dataKey="redeemed" stroke="#10B981" strokeWidth={2} /></LineChart></ResponsiveContainer></div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5"><p className="font-heading font-bold mb-3">Top merchants by verified visits</p>
            {d.top_merchants.length === 0 ? <p className="text-sm text-slate-500">No visits yet.</p> : d.top_merchants.map((m, i) => <div key={i} className="flex justify-between text-sm py-1.5 border-t border-slate-100"><span>{m.merchant}</span><b>{m.visits}</b></div>)}</div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 h-72"><p className="font-heading font-bold mb-2">Visits by category</p>
            <ResponsiveContainer width="100%" height="85%"><BarChart data={d.by_category}><XAxis dataKey="category" fontSize={10} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Bar dataKey="visits" fill="#FF3B30" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5"><p className="font-heading font-bold mb-3">Visits by city</p>{d.by_city.map((c) => <div key={c.city} className="flex justify-between text-sm py-1.5 border-t border-slate-100"><span>{c.city}</span><b>{c.visits}</b></div>)}</div>
        </div>
      </>)}
    </div>
  );
}

export default function AdminApp() {
  return (
    <PortalShell title="Super Admin" items={NAV}>
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="merchants" element={<AdminMerchants />} />
        <Route path="merchants/:id" element={<AdminMerchantDetail />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="deals" element={<AdminDeals />} />
        <Route path="categories" element={<AdminCategories />} />
        <Route path="packages" element={<AdminPackages />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="coupons" element={<AdminCoupons />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="audit" element={<AdminAudit />} />
        <Route path="settings" element={<AdminSettings />} />
      </Routes>
    </PortalShell>
  );
}
